import {memo, useCallback, useState, useMemo} from "react";
import {Badge, Box, Circle, Flex, HStack, Icon, Input, Stack, Text} from "@chakra-ui/react";
import {PlusIcon, PencilSquareIcon, TrashIcon, CheckIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {AutoResizingTextarea} from "../../../../../components/AutoResizingTextarea";
import {Button} from "../../../../../components/ui/button";
import {Checkbox} from "../../../../../components/ui/checkbox";
import {NativeSelectRoot, NativeSelectField} from "../../../../../components/ui/native-select";
import {EmptyState} from "../../../../../components/ui/empty-state";
import type {Finding, FindingRating, Control} from "../types";
import {STATUS_LABELS} from "../types";
import {CompactMarkdown} from "./CompactMarkdown";

// --- Constants ---

const RATING_CONFIG: Record<FindingRating, {label: string; colorPalette: string}> = {
  low: {label: "Low", colorPalette: "green"},
  moderate: {label: "Moderate", colorPalette: "yellow"},
  high: {label: "High", colorPalette: "orange"},
};

// --- Sub-components ---

const FindingRatingBadge = memo(({rating}: {rating: FindingRating}) => {
  const config = RATING_CONFIG[rating];
  return (
    <Badge colorPalette={config.colorPalette} variant="solid" size="sm">
      {config.label}
    </Badge>
  );
});

const ControlBadge = memo(({control}: {control: Control}) => (
  <Badge colorPalette="cyan" variant="outline" size="sm">
    {control.controlNumber} — {control.title}
  </Badge>
));

const FindingViewCard = memo(
  ({
    finding,
    index,
    linkedControl,
    onEdit,
    onDelete,
  }: {
    finding: Finding;
    index: number;
    linkedControl: Control | undefined;
    onEdit: () => void;
    onDelete: () => void;
  }) => (
    <Box borderRadius="md" border="1px solid {colors.gray.200}" overflow="hidden">
      {/* Header */}
      <Flex align="center" gap={2} px={4} py={3} bg="gray.100" borderBottom="1px solid {colors.gray.200}">
        <Text fontSize="sm" fontWeight="semibold" color="gray.500">
          #{index + 1}
        </Text>
        <FindingRatingBadge rating={finding.rating} />
        <HStack gap={1.5} flexShrink={0}>
          <Circle size="8px" bg="gray.400" />
          <Text fontSize="xs" color="gray.500">
            {STATUS_LABELS[finding.status]}
          </Text>
        </HStack>
        <Text fontSize="sm" fontWeight="semibold" color="gray.800" flex={1} truncate>
          {finding.internal.title}
        </Text>
        <HStack gap={1}>
          <Box
            as="button"
            p={1}
            borderRadius="sm"
            color="gray.400"
            _hover={{color: "blue.500", bg: "gray.50"}}
            cursor="pointer"
            onClick={onEdit}
            title="Edit finding"
          >
            <Box as={PencilSquareIcon} boxSize="14px" />
          </Box>
          <Box
            as="button"
            p={1}
            borderRadius="sm"
            color="gray.400"
            _hover={{color: "red.500", bg: "red.50"}}
            cursor="pointer"
            onClick={onDelete}
            title="Delete finding"
          >
            <Box as={TrashIcon} boxSize="14px" />
          </Box>
        </HStack>
      </Flex>

      {/* Internal section */}
      <Box px={4} py={3} bg="white" borderBottom="1px solid {colors.gray.200}">
        <Flex align="center" justify="space-between" mb={2}>
          <Badge colorPalette="purple" variant="subtle" size="sm">
            Internal
          </Badge>
          <Checkbox size="sm" checked={finding.includeInternal} readOnly cursor="default">
            <Text fontSize="xs" color="gray.500">
              Include in internal report
            </Text>
          </Checkbox>
        </Flex>
        {linkedControl && (
          <Box mb={2}>
            <ControlBadge control={linkedControl} />
          </Box>
        )}
        <CompactMarkdown>{finding.internal.content}</CompactMarkdown>
      </Box>

      {/* Client-facing section */}
      {finding.clientFacing && (
        <Box px={4} py={3} bg="white">
          <Flex align="center" justify="space-between" mb={2}>
            <Badge colorPalette="blue" variant="subtle" size="sm">
              Client-facing
            </Badge>
            <Checkbox size="sm" checked={finding.includeClientFacing} readOnly cursor="default">
              <Text fontSize="xs" color="gray.500">
                Include in client report
              </Text>
            </Checkbox>
          </Flex>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
            {finding.clientFacing.title}
          </Text>
          <CompactMarkdown>{finding.clientFacing.content}</CompactMarkdown>
        </Box>
      )}
    </Box>
  ),
);

const FindingEditForm = memo(
  ({
    finding,
    index,
    allControls,
    onSave,
    onDiscard,
  }: {
    finding: Finding;
    index: number;
    allControls: Control[];
    onSave: (updates: Partial<Finding>) => void;
    onDiscard: () => void;
  }) => {
    const [rating, setRating] = useState(finding.rating);
    const [internalTitle, setInternalTitle] = useState(finding.internal.title);
    const [internalContent, setInternalContent] = useState(finding.internal.content);
    const [controlId, setControlId] = useState(finding.controlId ?? "");
    const [hasClientFacing, setHasClientFacing] = useState(finding.clientFacing !== null);
    const [clientTitle, setClientTitle] = useState(finding.clientFacing?.title ?? "");
    const [clientContent, setClientContent] = useState(finding.clientFacing?.content ?? "");
    const [includeInternal, setIncludeInternal] = useState(finding.includeInternal);
    const [includeClientFacing, setIncludeClientFacing] = useState(finding.includeClientFacing);

    const handleSave = useCallback(() => {
      onSave({
        rating,
        internal: {title: internalTitle, content: internalContent},
        clientFacing: hasClientFacing ? {title: clientTitle, content: clientContent} : null,
        controlId: controlId || null,
        includeInternal,
        includeClientFacing: hasClientFacing && includeClientFacing,
      });
    }, [
      rating,
      internalTitle,
      internalContent,
      controlId,
      hasClientFacing,
      clientTitle,
      clientContent,
      includeInternal,
      includeClientFacing,
      onSave,
    ]);

    return (
      <Box borderRadius="md" border="1px solid {colors.blue.300}" overflow="hidden">
        {/* Header */}
        <Flex align="center" gap={2} px={4} py={3} bg="gray.100" borderBottom="1px solid {colors.gray.200}">
          <Text fontSize="sm" fontWeight="semibold" color="gray.500">
            #{index + 1}
          </Text>
          <NativeSelectRoot size="sm" w="130px">
            <NativeSelectField value={rating} onChange={e => setRating(e.target.value as FindingRating)}>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </NativeSelectField>
          </NativeSelectRoot>
          <Input
            size="sm"
            value={internalTitle}
            onChange={e => setInternalTitle(e.target.value)}
            placeholder="Finding title..."
            flex={1}
            bg="white"
          />
          <HStack gap={1}>
            <Button size="xs" variant="ghost" onClick={onDiscard}>
              <Icon asChild>
                <XMarkIcon />
              </Icon>
              Discard
            </Button>
            <Button size="xs" colorPalette="blue" onClick={handleSave}>
              <Icon asChild>
                <CheckIcon />
              </Icon>
              Save
            </Button>
          </HStack>
        </Flex>

        {/* Internal section */}
        <Box px={4} py={3} bg="white" borderBottom="1px solid {colors.gray.200}">
          <Flex align="center" justify="space-between" mb={2}>
            <Badge colorPalette="purple" variant="subtle" size="sm">
              Internal
            </Badge>
            <Checkbox
              size="sm"
              checked={includeInternal}
              onCheckedChange={(e: {checked: string | boolean}) => setIncludeInternal(!!e.checked)}
            >
              <Text fontSize="xs" color="gray.500">
                Include in internal report
              </Text>
            </Checkbox>
          </Flex>
          <Stack gap={2}>
            <NativeSelectRoot size="sm">
              <NativeSelectField value={controlId} onChange={e => setControlId(e.target.value)}>
                <option value="">No linked control</option>
                {allControls.map(ctrl => (
                  <option key={ctrl.id} value={ctrl.id}>
                    {ctrl.controlNumber} — {ctrl.title}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            <AutoResizingTextarea
              value={internalContent}
              onChange={e => setInternalContent(e.target.value)}
              fontSize="sm"
              minRows={3}
              placeholder="Internal finding content (markdown)..."
            />
          </Stack>
        </Box>

        {/* Client-facing section */}
        <Box px={4} py={3} bg="white">
          <Flex align="center" justify="space-between" mb={2}>
            <Checkbox
              size="sm"
              checked={hasClientFacing}
              onCheckedChange={(e: {checked: string | boolean}) => setHasClientFacing(!!e.checked)}
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                Has client-facing content
              </Text>
            </Checkbox>
            {hasClientFacing && (
              <Checkbox
                size="sm"
                checked={includeClientFacing}
                onCheckedChange={(e: {checked: string | boolean}) => setIncludeClientFacing(!!e.checked)}
              >
                <Text fontSize="xs" color="gray.500">
                  Include in client report
                </Text>
              </Checkbox>
            )}
          </Flex>
          {hasClientFacing && (
            <Stack gap={2}>
              <Input
                size="sm"
                value={clientTitle}
                onChange={e => setClientTitle(e.target.value)}
                placeholder="Client-facing title..."
                bg="white"
              />
              <AutoResizingTextarea
                value={clientContent}
                onChange={e => setClientContent(e.target.value)}
                fontSize="sm"
                minRows={3}
                placeholder="Client-facing content (markdown)..."
              />
            </Stack>
          )}
        </Box>
      </Box>
    );
  },
);

// --- Main list component ---

export const FindingsCardList = memo(
  ({
    findings,
    addFinding,
    updateFinding,
    deleteFinding,
    allControls,
  }: {
    findings: Finding[];
    addFinding: () => void;
    updateFinding: (id: string, updates: Partial<Finding>) => void;
    deleteFinding: (id: string) => void;
    allControls: Control[];
  }) => {
    const [editingId, setEditingId] = useState<string | null>(null);

    const controlMap = useMemo(() => {
      const map = new Map<string, Control>();
      for (const ctrl of allControls) map.set(ctrl.id, ctrl);
      return map;
    }, [allControls]);

    const handleSave = useCallback(
      (id: string, updates: Partial<Finding>) => {
        updateFinding(id, updates);
        setEditingId(null);
      },
      [updateFinding],
    );

    return (
      <Stack gap={3}>
        <Flex align="center" justify="end" px={1}>
          <HStack
            as="button"
            gap={1}
            cursor="pointer"
            fontSize="sm"
            fontWeight={550}
            color="gray.500"
            _hover={{color: "gray.700"}}
            onClick={addFinding}
          >
            <Box as={PlusIcon} boxSize="16px" />
            Add finding
          </HStack>
        </Flex>

        {findings.length === 0 ? (
          <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" p={8}>
            <EmptyState title="No findings" description="Add a finding to document audit observations for this area." />
          </Box>
        ) : (
          <Stack gap={3}>
            {findings.map((finding, i) =>
              editingId === finding.id ? (
                <FindingEditForm
                  key={finding.id}
                  finding={finding}
                  index={i}
                  allControls={allControls}
                  onSave={updates => handleSave(finding.id, updates)}
                  onDiscard={() => setEditingId(null)}
                />
              ) : (
                <FindingViewCard
                  key={finding.id}
                  finding={finding}
                  index={i}
                  linkedControl={finding.controlId ? controlMap.get(finding.controlId) : undefined}
                  onEdit={() => setEditingId(finding.id)}
                  onDelete={() => deleteFinding(finding.id)}
                />
              ),
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);
